import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Package, Search, Trash2, DollarSign, FileText, Download, Printer, Tag, CreditCard, Filter, CalendarDays, X, Camera, Eye, Image, Edit, MessageCircle, Truck, Video, Play, AlertTriangle, Link, Save, Phone, MapPin, User, IdCard } from 'lucide-react';
import ShipmentForm from '../components/ShipmentForm';
import CustomerSearch from '../components/CustomerSearch';
import BatchRelinkDialog from '../components/BatchRelinkDialog';
import QuickEditDialog from '../components/QuickEditDialog';

const ESTADOS = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-gray-100 text-gray-800 border-gray-300' },
  { value: 'recogido', label: 'Recogido', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'en_transito', label: 'En Transito', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  { value: 'embarque_maritimo', label: 'Embarque Maritimo', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  { value: 'revision_aduana', label: 'Revision Aduana', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'almacen_rd', label: 'Almacen RD', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'en_furgon', label: 'En Furgon', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'camino_entrega', label: 'En Camino', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'entregado', label: 'Entregado', color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-800 border-red-300' },
];

const Shipments = () => {
  const [shipments, setShipments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ estado_pago: '', fecha_desde: '', fecha_hasta: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [abonoDialogOpen, setAbonoDialogOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [abonos, setAbonos] = useState([]);
  const [abonoForm, setAbonoForm] = useState({ monto: 0, metodo_pago: 'efectivo', notas: '' });
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailShipment, setDetailShipment] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [openFurgones, setOpenFurgones] = useState([]);
  const [editingShipment, setEditingShipment] = useState(null);
  const [relinkDialog, setRelinkDialog] = useState({ open: false, shipmentId: null, field: null });
  const [quickEditDialog, setQuickEditDialog] = useState({ open: false, customer: null, shipmentId: null });
  const [quickEditForm, setQuickEditForm] = useState({});
  const [savingQuickEdit, setSavingQuickEdit] = useState(false);
  const [batchRelinkOpen, setBatchRelinkOpen] = useState(false);
  const [orphanCount, setOrphanCount] = useState(0);

  useEffect(() => {
    fetchShipments();
    fetchCustomers();
    fetchOpenFurgones();
    checkOrphans();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchShipments(), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filters.estado_pago, filters.fecha_desde, filters.fecha_hasta]);

  const fetchShipments = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filters.estado_pago) params.estado_pago = filters.estado_pago;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      const response = await api.get('/shipments', { params });
      setShipments(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar envios');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const [remRes, destRes] = await Promise.all([
        api.get('/customers', { params: { tipo: 'remitente', limit: 100 } }),
        api.get('/customers', { params: { tipo: 'destinatario', limit: 100 } })
      ]);
      const rem = remRes.data.data || [];
      const dest = destRes.data.data || [];
      setCustomers([...rem, ...dest]);
    } catch (error) {
      console.error('Error loading customers');
    }
  };

  const fetchOpenFurgones = async () => {
    try {
      const response = await api.get('/furgones', { params: { abierto: 'true' } });
      setOpenFurgones(response.data.data || []);
    } catch (error) {
      console.error('Error loading furgones');
    }
  };

  const checkOrphans = async () => {
    try {
      const res = await api.get('/shipments/diagnostico/clientes-huerfanos');
      const total = (res.data.orphaned_remitentes || 0) + (res.data.orphaned_destinatarios || 0);
      setOrphanCount(total);
    } catch { /* ignore */ }
  };

  // === MISSING DATA & RELINK HELPERS ===
  const REQUIRED_FIELDS = [
    { key: 'nombre_completo', label: 'Nombre', icon: 'user' },
    { key: 'cedula_pasaporte', label: 'Cedula/ID', icon: 'id' },
    { key: 'telefono', label: 'Telefono', icon: 'phone' },
    { key: 'direccion_completa', label: 'Direccion', icon: 'map' },
  ];

  const getMissingFields = (customer) => {
    if (!customer) return [];
    return REQUIRED_FIELDS.filter(f => !customer[f.key] || customer[f.key].trim() === '');
  };

  const hasIncompleteData = (shipment) => {
    const remMissing = shipment.remitente ? getMissingFields(shipment.remitente) : [];
    const destMissing = shipment.destinatario ? getMissingFields(shipment.destinatario) : [];
    return remMissing.length > 0 || destMissing.length > 0;
  };

  const handleRelink = async (customerId) => {
    if (!relinkDialog.shipmentId || !relinkDialog.field) return;
    try {
      const payload = relinkDialog.field === 'remitente'
        ? { remitente_id: customerId }
        : { destinatario_id: customerId };
      await api.patch(`/shipments/${relinkDialog.shipmentId}/relink`, payload);
      toast.success('Cliente enlazado exitosamente');
      setRelinkDialog({ open: false, shipmentId: null, field: null });
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enlazar cliente');
    }
  };

  const openQuickEdit = (customer, shipmentId) => {
    setQuickEditForm({
      nombre_completo: customer.nombre_completo || '',
      cedula_pasaporte: customer.cedula_pasaporte || '',
      telefono: customer.telefono || '',
      telefono_alternativo: customer.telefono_alternativo || '',
      direccion_completa: customer.direccion_completa || '',
      apartamento: customer.apartamento || '',
      ciudad: customer.ciudad || '',
      provincia: customer.provincia || '',
      email: customer.email || '',
    });
    setQuickEditDialog({ open: true, customer, shipmentId });
  };

  const handleQuickEditSave = async () => {
    if (!quickEditDialog.customer) return;
    setSavingQuickEdit(true);
    try {
      const updates = {};
      for (const [key, val] of Object.entries(quickEditForm)) {
        if (val && val.trim() !== '' && val !== (quickEditDialog.customer[key] || '')) {
          updates[key] = val.trim();
        }
      }
      if (Object.keys(updates).length === 0) {
        toast.info('No hay cambios para guardar');
        setSavingQuickEdit(false);
        return;
      }
      await api.patch(`/customers/${quickEditDialog.customer.id}/quick-update`, updates);
      toast.success('Datos del cliente actualizados');
      setQuickEditDialog({ open: false, customer: null, shipmentId: null });
      fetchShipments();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSavingQuickEdit(false);
    }
  };

  // Form submit handler
  const handleFormSubmit = async (formData, firmaRemitente, firmaRecolector, customNumber) => {
    if (editingShipment) {
      await api.put(`/shipments/${editingShipment.id}`, formData);
      toast.success('Factura actualizada');
    } else {
      const url = customNumber ? `/shipments?numero_personalizado=${encodeURIComponent(customNumber)}` : '/shipments';
      const response = await api.post(url, formData);
      const newId = response.data.id;
      if (firmaRemitente || firmaRecolector) {
        try {
          await api.post(`/shipments/${newId}/firmas`, {
            firma_remitente: firmaRemitente,
            firma_recolector: firmaRecolector
          });
        } catch (e) { console.error('Error saving signatures'); }
      }
      toast.success(`Factura ${response.data.numero_factura} creada`);
    }
    fetchShipments();
    setDialogOpen(false);
    setEditingShipment(null);
  };

  // Edit shipment
  const handleEditShipment = (shipment) => {
    setEditingShipment(shipment);
    setDialogOpen(true);
  };

  // Delete shipment
  const handleDeleteShipment = async (shipment) => {
    if (!window.confirm(`Eliminar factura ${shipment.numero_factura}? Esta accion no se puede deshacer.`)) return;
    try {
      await api.delete(`/shipments/${shipment.id}`);
      toast.success(`Factura ${shipment.numero_factura} eliminada`);
      fetchShipments();
    } catch (error) {
      toast.error('Error al eliminar factura');
    }
  };

  // Share via WhatsApp
  const shareWhatsApp = (shipment) => {
    const tel = shipment.destinatario?.telefono || shipment.remitente?.telefono || '';
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const pdfUrl = `${backendUrl}/api/shipments/public/factura/${shipment.id}`;
    const msg = `Factura ${shipment.numero_factura} - DOMINICAN SHIPPING\nTotal: $${shipment.total?.toFixed(2)}\nPDF: ${pdfUrl}`;
    const whatsappUrl = `https://wa.me/${tel.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(msg)}`;
    window.open(whatsappUrl, '_blank');
  };

  // PDF Actions
  const viewFactura = async (shipmentId) => {
    try {
      const response = await api.get(`/shipments/${shipmentId}/factura`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Error al ver factura');
    }
  };

  const downloadFactura = async (shipmentId, numFactura) => {
    try {
      const response = await api.get(`/shipments/${shipmentId}/factura/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${numFactura}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Factura descargada');
    } catch (error) {
      toast.error('Error al descargar factura');
    }
  };

  const printFactura = async (shipmentId) => {
    try {
      const response = await api.get(`/shipments/${shipmentId}/factura`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      printWindow.addEventListener('load', () => { printWindow.print(); });
    } catch (error) {
      toast.error('Error al imprimir factura');
    }
  };

  const printEtiquetas = async (shipmentId) => {
    try {
      const response = await api.get(`/shipments/${shipmentId}/etiquetas`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      printWindow.addEventListener('load', () => { printWindow.print(); });
    } catch (error) {
      toast.error('Error al imprimir etiquetas');
    }
  };

  // Detail / Photos
  const openDetail = async (shipment) => {
    try {
      const res = await api.get(`/shipments/${shipment.id}`);
      setDetailShipment(res.data);
    } catch { setDetailShipment(shipment); }
    setDetailDialog(true);
  };

  const handlePhotoUpload = async (shipmentId, type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      setUploadingPhoto(true);
      try {
        const fd = new FormData();
        files.forEach(file => fd.append('files', file));
        const endpoint = type === 'recogida' ? 'foto-recogida' : 'foto-entrega';
        const res = await api.post(`/shipments/${shipmentId}/${endpoint}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(res.data.message);
        const updated = await api.get(`/shipments/${shipmentId}`);
        setDetailShipment(updated.data);
        fetchShipments();
      } catch (error) {
        toast.error('Error al subir archivos');
      } finally {
        setUploadingPhoto(false);
      }
    };
    input.click();
  };

  const isVideo = (url) => /\.(mp4|mov|avi|wmv|webm|mkv)$/i.test(url);

  const MediaGallery = ({ items, emptyText }) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <Image className="h-12 w-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-400">{emptyText}</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((url, idx) => (
          <div key={idx} className="relative group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all">
            {isVideo(url) ? (
              <video
                src={url}
                controls
                className="w-full h-32 object-cover bg-black"
                data-testid={`media-video-${idx}`}
              />
            ) : (
              <a href={url} target="_blank" rel="noopener noreferrer">
                <img
                  src={url}
                  alt={`Media ${idx + 1}`}
                  className="w-full h-32 object-cover hover:scale-105 transition-transform"
                  data-testid={`media-image-${idx}`}
                />
              </a>
            )}
            <div className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
              {isVideo(url) ? <Video className="h-3 w-3 inline" /> : <Camera className="h-3 w-3 inline" />}
              {' '}{idx + 1}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Abonos
  const openAbonoDialog = async (shipment) => {
    setSelectedShipment(shipment);
    setAbonoForm({ monto: 0, metodo_pago: 'efectivo', notas: '' });
    try {
      const response = await api.get(`/shipments/${shipment.id}/abonos`);
      setAbonos(response.data || []);
    } catch (error) {
      setAbonos([]);
    }
    setAbonoDialogOpen(true);
  };

  const handleAbono = async (e) => {
    e.preventDefault();
    if (abonoForm.monto <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    try {
      const response = await api.post(`/shipments/${selectedShipment.id}/abono`, abonoForm);
      toast.success('Abono registrado exitosamente');
      const abonosRes = await api.get(`/shipments/${selectedShipment.id}/abonos`);
      setAbonos(abonosRes.data || []);
      setSelectedShipment(prev => ({
        ...prev,
        monto_pagado: response.data.shipment.monto_pagado,
        monto_pendiente: response.data.shipment.monto_pendiente,
        estado_pago: response.data.shipment.estado_pago
      }));
      setAbonoForm({ monto: 0, metodo_pago: 'efectivo', notas: '' });
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar abono');
    }
  };

  const remitentes = customers.filter(c => c.tipo === 'remitente' || c.tipo === 'ambos');
  const destinatarios = customers.filter(c => c.tipo === 'destinatario' || c.tipo === 'ambos');

  const handleStatusChange = async (shipment, newEstado) => {
    try {
      await api.put(`/shipments/${shipment.id}/estado?estado=${newEstado}`);
      toast.success(`${shipment.numero_factura} → ${ESTADOS.find(e => e.value === newEstado)?.label || newEstado}`);
      fetchShipments();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const getEstadoStyle = (estado) => ESTADOS.find(e => e.value === estado)?.color || 'bg-gray-100 text-gray-800 border-gray-300';

  const activeFilterCount = [filters.estado_pago, filters.fecha_desde, filters.fecha_hasta].filter(Boolean).length;
  const clearFilters = () => setFilters({ estado_pago: '', fecha_desde: '', fecha_hasta: '' });

  const downloadExcelReport = async () => {
    try {
      const response = await api.get('/reports/envios/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_envios_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch (error) {
      toast.error('Error al descargar reporte');
    }
  };

  const estadoPagoBadge = (estado) => {
    const styles = {
      pagado: 'bg-green-100 text-green-800 border-green-300',
      parcial: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      pendiente: 'bg-red-100 text-red-800 border-red-300'
    };
    const labels = { pagado: 'Pagado', parcial: 'Parcial', pendiente: 'Pendiente' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[estado] || styles.pendiente}`}>
        {labels[estado] || estado}
      </span>
    );
  };

  // Customer cell with alert for missing data or N/A relink
  const CustomerCell = ({ customer, shipment, field, compact }) => {
    if (!customer) {
      return (
        <div className="flex items-center gap-1">
          <span className="text-gray-400 text-sm">N/A</span>
          <button
            data-testid={`relink-${field}-${shipment.id}`}
            onClick={() => setRelinkDialog({ open: true, shipmentId: shipment.id, field })}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
            title={`Enlazar ${field === 'remitente' ? 'remitente' : 'destinatario'}`}
          >
            <Link className="h-3 w-3" /> Enlazar
          </button>
        </div>
      );
    }

    const missing = getMissingFields(customer);
    return (
      <div className="space-y-0.5">
        <span className={`font-semibold ${compact ? 'text-sm' : 'text-base'} block truncate`}>
          {customer.nombre_completo || 'Sin nombre'}
        </span>
        {missing.length > 0 && (
          <button
            data-testid={`incomplete-${field}-${shipment.id}`}
            onClick={() => openQuickEdit(customer, shipment.id)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium hover:bg-amber-100 transition-colors border border-amber-300 leading-tight"
            title="Completar datos faltantes"
          >
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            Falta: {missing.map(m => m.label).join(', ')}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-3 sm:p-6 pb-24">
      <div className="max-w-7xl mx-auto">
        <Card className="shadow-xl border-2 border-blue-100">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Package className="h-8 w-8" />
                </div>
                <div>
                  <CardTitle className="text-2xl md:text-3xl font-bold">Envios / Facturas</CardTitle>
                  <p className="text-blue-100 text-base">Gestion de envios y facturacion</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  data-testid="download-excel-report"
                  variant="secondary"
                  onClick={downloadExcelReport}
                  className="bg-white/20 text-white hover:bg-white/30 border-white/40 h-12 text-base px-4"
                >
                  <Download className="mr-2 h-5 w-5" /> Excel
                </Button>
                <Badge variant="secondary" className="text-lg px-4 py-2">
                  {shipments.length} facturas
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            {/* Search & Filters */}
            <div className="mb-6 space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    data-testid="search-shipments"
                    placeholder="Buscar por factura, nombre, telefono, cedula..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12 text-lg border-2 focus:border-blue-500"
                  />
                </div>
                <Button
                  data-testid="toggle-filters-btn"
                  variant={showFilters ? "default" : "outline"}
                  onClick={() => setShowFilters(!showFilters)}
                  className={`h-12 px-4 ${showFilters ? 'bg-blue-600 text-white' : ''}`}
                >
                  <Filter className="mr-2 h-5 w-5" />
                  Filtros {activeFilterCount > 0 && <Badge className="ml-2 bg-red-500 text-white">{activeFilterCount}</Badge>}
                </Button>
                <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingShipment(null); }}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="create-shipment-btn"
                      onClick={() => setEditingShipment(null)}
                      className="h-12 px-4 sm:px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg text-sm sm:text-base"
                      size="lg"
                    >
                      <Plus className="mr-1 sm:mr-2 h-5 w-5" />
                      <span className="hidden sm:inline">Crear Factura</span>
                      <span className="sm:hidden">Nueva</span>
                    </Button>
                  </DialogTrigger>

                  {orphanCount > 0 && (
                    <Button
                      data-testid="batch-relink-btn"
                      onClick={() => setBatchRelinkOpen(true)}
                      variant="outline"
                      className="h-12 px-4 border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-800 text-sm font-semibold"
                    >
                      <AlertTriangle className="mr-1 h-4 w-4 text-amber-600" />
                      <span className="hidden sm:inline">Re-enlazar ({orphanCount})</span>
                      <span className="sm:hidden">{orphanCount} N/A</span>
                    </Button>
                  )}
                  <DialogContent className="w-full max-w-4xl h-[100vh] sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col p-0 sm:p-6 gap-0" aria-describedby="shipment-form-desc">
                    <DialogHeader className="p-4 sm:p-0 pb-2 sm:pb-4 border-b sm:border-b-0 flex-shrink-0">
                      <DialogTitle className="text-xl flex items-center gap-2">
                        <Package className="h-5 w-5 text-blue-600" />
                        {editingShipment ? `Editar ${editingShipment.numero_factura}` : 'Nueva Factura'}
                      </DialogTitle>
                      <p id="shipment-form-desc" className="text-xs sm:text-sm text-gray-500">
                        Selecciona cliente, productos y pago
                      </p>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto px-4 sm:px-0 pb-4">
                      <ShipmentForm
                        editingShipment={editingShipment}
                        remitentes={remitentes}
                        destinatarios={destinatarios}
                        openFurgones={openFurgones}
                        onSubmit={handleFormSubmit}
                        onCancel={() => { setDialogOpen(false); setEditingShipment(null); }}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Advanced Filters Panel */}
              {showFilters && (
                <div data-testid="filters-panel" className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-base text-blue-800 flex items-center gap-2">
                      <Filter className="h-4 w-4" /> Filtros Avanzados
                    </span>
                    {activeFilterCount > 0 && (
                      <Button data-testid="clear-filters-btn" variant="ghost" size="sm" onClick={clearFilters} className="text-red-600 hover:bg-red-50">
                        <X className="mr-1 h-4 w-4" /> Limpiar
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-sm font-semibold text-gray-700">Estado de Pago</Label>
                      <Select value={filters.estado_pago} onValueChange={(v) => setFilters({...filters, estado_pago: v === 'todos' ? '' : v})}>
                        <SelectTrigger data-testid="filter-estado-pago" className="h-11 mt-1"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          <SelectItem value="pagado">Pagado</SelectItem>
                          <SelectItem value="pendiente">Pendiente</SelectItem>
                          <SelectItem value="parcial">Parcial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" /> Desde
                      </Label>
                      <Input
                        data-testid="filter-fecha-desde"
                        type="date"
                        value={filters.fecha_desde}
                        onChange={(e) => setFilters({...filters, fecha_desde: e.target.value})}
                        className="h-11 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" /> Hasta
                      </Label>
                      <Input
                        data-testid="filter-fecha-hasta"
                        type="date"
                        value={filters.fecha_hasta}
                        onChange={(e) => setFilters({...filters, fecha_hasta: e.target.value})}
                        className="h-11 mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Shipments - Mobile Cards + Desktop Table */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Cargando envios...</p>
              </div>
            ) : shipments.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No hay envios registrados</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View - Optimizado */}
                <div className="sm:hidden space-y-3">
                  {shipments.map((shipment) => (
                    <div key={shipment.id} className="bg-white rounded-xl border-2 border-gray-100 shadow-sm" data-testid={`shipment-row-${shipment.id}`}>
                      {/* Header con factura y estado de pago */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`font-black text-lg ${shipment.is_personalizado ? 'text-purple-600' : 'text-blue-600'}`}>{shipment.numero_factura}</span>
                            {shipment.numero_furgon && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-200" data-testid={`furgon-badge-${shipment.id}`}>
                                <Truck className="h-3 w-3" /> F-{shipment.numero_furgon}
                              </span>
                            )}
                          </div>
                          {estadoPagoBadge(shipment.estado_pago)}
                        </div>
                        
                        {/* Estado dropdown - Full width */}
                        <Select value={shipment.estado} onValueChange={(v) => handleStatusChange(shipment, v)}>
                          <SelectTrigger data-testid={`estado-select-${shipment.id}`} className={`h-10 text-sm font-bold border ${getEstadoStyle(shipment.estado)}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ESTADOS.map(e => (
                              <SelectItem key={e.value} value={e.value}>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${e.color}`}>{e.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Clientes y Total */}
                      <div className="p-3">
                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          <div className="bg-blue-50 rounded-lg p-2">
                            <span className="text-blue-400 text-xs block">De:</span>
                            <CustomerCell customer={shipment.remitente} shipment={shipment} field="remitente" compact />
                          </div>
                          <div className="bg-green-50 rounded-lg p-2">
                            <span className="text-green-400 text-xs block">Para:</span>
                            <CustomerCell customer={shipment.destinatario} shipment={shipment} field="destinatario" compact />
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <span className="font-black text-green-600 text-2xl">${shipment.total?.toFixed(2)}</span>
                            {shipment.monto_pendiente > 0 && (
                              <span className="text-red-500 font-bold text-sm ml-2">Pend: ${(shipment.monto_pendiente || 0).toFixed(2)}</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons - Grid layout optimizado para móvil */}
                        <div className="grid grid-cols-5 gap-2">
                          <Button data-testid={`view-detail-${shipment.id}`} variant="outline" onClick={() => openDetail(shipment)} className="h-12 w-full p-0 border-green-300 text-green-600 flex flex-col items-center justify-center">
                            <Eye className="h-5 w-5" />
                            <span className="text-[10px] mt-0.5">Ver</span>
                          </Button>
                          <Button data-testid={`view-factura-${shipment.id}`} variant="outline" onClick={() => viewFactura(shipment.id)} className="h-12 w-full p-0 flex flex-col items-center justify-center">
                            <FileText className="h-5 w-5" />
                            <span className="text-[10px] mt-0.5">PDF</span>
                          </Button>
                          <Button data-testid={`print-etiquetas-${shipment.id}`} variant="outline" onClick={() => printEtiquetas(shipment.id)} className="h-12 w-full p-0 flex flex-col items-center justify-center">
                            <Tag className="h-5 w-5" />
                            <span className="text-[10px] mt-0.5">Etiq.</span>
                          </Button>
                          <Button data-testid={`edit-shipment-${shipment.id}`} variant="outline" onClick={() => handleEditShipment(shipment)} className="h-12 w-full p-0 border-indigo-300 text-indigo-600 flex flex-col items-center justify-center">
                            <Edit className="h-5 w-5" />
                            <span className="text-[10px] mt-0.5">Editar</span>
                          </Button>
                          <Button data-testid={`whatsapp-shipment-${shipment.id}`} variant="outline" onClick={() => shareWhatsApp(shipment)} className="h-12 w-full p-0 border-green-400 text-green-600 flex flex-col items-center justify-center">
                            <MessageCircle className="h-5 w-5" />
                            <span className="text-[10px] mt-0.5">WA</span>
                          </Button>
                        </div>
                        
                        {/* Secondary actions row */}
                        <div className="flex gap-2 mt-2">
                          {shipment.monto_pendiente > 0 && (
                            <Button data-testid={`abono-btn-${shipment.id}`} className="flex-1 h-10 bg-amber-500 hover:bg-amber-600 text-sm" onClick={() => openAbonoDialog(shipment)}>
                              <CreditCard className="mr-1 h-4 w-4" /> Abono
                            </Button>
                          )}
                          <Button data-testid={`download-factura-${shipment.id}`} variant="outline" onClick={() => downloadFactura(shipment.id, shipment.numero_factura)} className="h-10 px-3">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button data-testid={`delete-shipment-${shipment.id}`} variant="outline" onClick={() => handleDeleteShipment(shipment)} className="h-10 px-3 border-red-300 text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden sm:block bg-white rounded-lg border-2 border-gray-100 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-blue-50 to-indigo-50">
                        <TableHead className="font-bold text-gray-900 text-base">Factura</TableHead>
                        <TableHead className="font-bold text-gray-900 text-base">Furgon</TableHead>
                        <TableHead className="font-bold text-gray-900 text-base">Remitente</TableHead>
                        <TableHead className="font-bold text-gray-900 text-base">Destinatario</TableHead>
                        <TableHead className="font-bold text-gray-900 text-base">Estado</TableHead>
                        <TableHead className="font-bold text-gray-900 text-base">Pago</TableHead>
                        <TableHead className="font-bold text-gray-900 text-base">Total</TableHead>
                        <TableHead className="font-bold text-gray-900 text-base">Pendiente</TableHead>
                        <TableHead className="font-bold text-gray-900 text-base">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shipments.map((shipment) => (
                        <TableRow key={shipment.id} className="hover:bg-blue-50/50 transition-colors" data-testid={`shipment-row-${shipment.id}`}>
                          <TableCell className={`font-bold text-lg ${shipment.is_personalizado ? 'text-purple-600' : 'text-blue-600'}`}>{shipment.numero_factura}</TableCell>
                          <TableCell>
                            {shipment.numero_furgon ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-sm font-bold border border-indigo-200" data-testid={`furgon-badge-${shipment.id}`}>
                                <Truck className="h-3.5 w-3.5" /> {shipment.numero_furgon}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-sm">Sin furgon</span>
                            )}
                          </TableCell>
                          <TableCell><CustomerCell customer={shipment.remitente} shipment={shipment} field="remitente" /></TableCell>
                          <TableCell><CustomerCell customer={shipment.destinatario} shipment={shipment} field="destinatario" /></TableCell>
                          <TableCell>
                            <Select value={shipment.estado} onValueChange={(v) => handleStatusChange(shipment, v)}>
                              <SelectTrigger data-testid={`estado-select-${shipment.id}`} className={`h-9 w-44 text-xs font-bold border ${getEstadoStyle(shipment.estado)}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ESTADOS.map(e => (
                                  <SelectItem key={e.value} value={e.value}>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${e.color}`}>{e.label}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>{estadoPagoBadge(shipment.estado_pago)}</TableCell>
                          <TableCell className="font-bold text-green-600 text-lg">${shipment.total?.toFixed(2)}</TableCell>
                          <TableCell className={`text-lg ${shipment.monto_pendiente > 0 ? 'font-bold text-red-600' : 'text-gray-400'}`}>
                            ${(shipment.monto_pendiente || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1.5 flex-wrap">
                              <Button data-testid={`view-detail-${shipment.id}`} variant="outline" size="sm" onClick={() => openDetail(shipment)} title="Ver Detalle" className="h-10 w-10 border-green-300 text-green-600 hover:bg-green-50">
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button data-testid={`view-factura-${shipment.id}`} variant="outline" size="sm" onClick={() => viewFactura(shipment.id)} title="Ver Factura" className="h-10 w-10">
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button data-testid={`download-factura-${shipment.id}`} variant="outline" size="sm" onClick={() => downloadFactura(shipment.id, shipment.numero_factura)} title="Descargar" className="h-10 w-10">
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button data-testid={`print-factura-${shipment.id}`} variant="outline" size="sm" onClick={() => printFactura(shipment.id)} title="Imprimir" className="h-10 w-10">
                                <Printer className="h-4 w-4" />
                              </Button>
                              <Button data-testid={`print-etiquetas-${shipment.id}`} variant="outline" size="sm" onClick={() => printEtiquetas(shipment.id)} title="Etiquetas" className="h-10 w-10">
                                <Tag className="h-4 w-4" />
                              </Button>
                              {shipment.monto_pendiente > 0 && (
                                <Button data-testid={`abono-btn-${shipment.id}`} variant="default" size="sm" className="bg-amber-500 hover:bg-amber-600 h-10 w-10" onClick={() => openAbonoDialog(shipment)} title="Abono">
                                  <CreditCard className="h-4 w-4" />
                                </Button>
                              )}
                              <Button data-testid={`edit-shipment-${shipment.id}`} variant="outline" size="sm" onClick={() => handleEditShipment(shipment)} title="Editar" className="h-10 w-10 border-indigo-300 text-indigo-600 hover:bg-indigo-50">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button data-testid={`delete-shipment-${shipment.id}`} variant="outline" size="sm" onClick={() => handleDeleteShipment(shipment)} title="Eliminar" className="h-10 w-10 border-red-300 text-red-600 hover:bg-red-50">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button data-testid={`whatsapp-shipment-${shipment.id}`} variant="outline" size="sm" onClick={() => shareWhatsApp(shipment)} title="WhatsApp" className="h-10 w-10 border-green-400 text-green-600 hover:bg-green-50">
                                <MessageCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Abono Dialog */}
        <Dialog open={abonoDialogOpen} onOpenChange={setAbonoDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="abono-dialog" aria-describedby="abono-dialog-desc">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-amber-500" />
                Abono a Factura {selectedShipment?.numero_factura}
              </DialogTitle>
              <p id="abono-dialog-desc" className="text-sm text-gray-500">Registrar pago parcial a esta factura</p>
            </DialogHeader>
            {selectedShipment && (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-500 uppercase font-semibold">Total</p>
                      <p className="text-2xl font-bold">${selectedShipment.total?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 uppercase font-semibold">Pagado</p>
                      <p className="text-2xl font-bold text-green-600">${selectedShipment.monto_pagado?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 uppercase font-semibold">Pendiente</p>
                      <p className="text-2xl font-bold text-red-600">${selectedShipment.monto_pendiente?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
                <form onSubmit={handleAbono} className="space-y-3">
                  <div>
                    <Label className="font-semibold">Monto del Abono *</Label>
                    <Input
                      data-testid="abono-monto-input"
                      type="number" step="0.01" min="0.01"
                      max={selectedShipment.monto_pendiente}
                      value={abonoForm.monto}
                      onChange={(e) => setAbonoForm({...abonoForm, monto: parseFloat(e.target.value) || 0})}
                      className="h-11 mt-1"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label className="font-semibold">Metodo de Pago</Label>
                    <Select value={abonoForm.metodo_pago} onValueChange={(v) => setAbonoForm({...abonoForm, metodo_pago: v})}>
                      <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="zelle">Zelle</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="pago_rd">Pago en RD</SelectItem>
                        <SelectItem value="transferencia_rd">Transferencia RD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-semibold">Notas</Label>
                    <Input
                      data-testid="abono-notas-input"
                      value={abonoForm.notas}
                      onChange={(e) => setAbonoForm({...abonoForm, notas: e.target.value})}
                      placeholder="Notas opcionales..."
                      className="h-11 mt-1"
                    />
                  </div>
                  <Button data-testid="submit-abono-btn" type="submit" className="w-full bg-amber-500 hover:bg-amber-600 h-11">
                    <CreditCard className="mr-2 h-4 w-4" /> Registrar Abono
                  </Button>
                </form>
                {abonos.length > 0 && (
                  <div>
                    <h4 className="font-bold text-base text-gray-700 mb-2">Historial de Abonos</h4>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {abonos.map((a) => (
                        <div key={a.id} className="flex justify-between items-center p-3 bg-gray-50 rounded border text-base">
                          <div>
                            <span className="font-bold text-green-600 text-lg">${a.monto?.toFixed(2)}</span>
                            <span className="text-gray-500 ml-2">{a.metodo_pago}</span>
                            {a.notas && <span className="text-gray-400 ml-2">- {a.notas}</span>}
                          </div>
                          <span className="text-sm text-gray-400">{new Date(a.fecha).toLocaleDateString('es')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Detail / Photos Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="detail-dialog-desc">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Eye className="h-5 w-5 text-green-500" />
                Detalle Envio {detailShipment?.numero_factura}
              </DialogTitle>
              <p id="detail-dialog-desc" className="text-sm text-gray-500">Firmas, fotos de recogida y entrega</p>
            </DialogHeader>
            {detailShipment && (
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <span className="text-base font-semibold">Estado:</span>
                  <Badge className="text-base px-3 py-1">{detailShipment.estado}</Badge>
                </div>
                {/* Signatures */}
                <div>
                  <h4 className="font-bold text-base text-gray-700 mb-2">Firmas</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border-2 rounded-lg p-3 text-center">
                      <p className="text-sm font-semibold text-gray-500 mb-2">Firma Remitente</p>
                      {detailShipment.firma_remitente ? (
                        <img src={detailShipment.firma_remitente} alt="Firma Remitente" className="h-20 mx-auto border rounded" />
                      ) : (
                        <p className="text-gray-400 text-sm py-4">Sin firma</p>
                      )}
                    </div>
                    <div className="border-2 rounded-lg p-3 text-center">
                      <p className="text-sm font-semibold text-gray-500 mb-2">Firma Recolector</p>
                      {detailShipment.firma_recolector ? (
                        <img src={detailShipment.firma_recolector} alt="Firma Recolector" className="h-20 mx-auto border rounded" />
                      ) : (
                        <p className="text-gray-400 text-sm py-4">Sin firma</p>
                      )}
                    </div>
                  </div>
                </div>
                {/* Pickup Photos/Videos */}
                <div className="border-2 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-base text-gray-700 flex items-center gap-2">
                      <Camera className="h-5 w-5 text-blue-500" /> Fotos/Videos de Recogida
                      {(detailShipment.fotos_recogida?.length || 0) > 0 && (
                        <Badge variant="secondary" className="ml-1">{detailShipment.fotos_recogida.length}</Badge>
                      )}
                    </h4>
                    <Button
                      data-testid="upload-pickup-photo-btn"
                      variant="outline" size="sm"
                      onClick={() => handlePhotoUpload(detailShipment.id, 'recogida')}
                      disabled={uploadingPhoto}
                      className="border-blue-300 text-blue-600"
                    >
                      <Camera className="mr-1 h-4 w-4" />
                      {uploadingPhoto ? 'Subiendo...' : 'Agregar'}
                    </Button>
                  </div>
                  <MediaGallery
                    items={detailShipment.fotos_recogida?.length ? detailShipment.fotos_recogida : (detailShipment.foto_recogida ? [detailShipment.foto_recogida] : [])}
                    emptyText="Sin fotos/videos de recogida"
                  />
                  {detailShipment.fecha_recogida && (
                    <p className="text-sm text-gray-500 text-center mt-2">Recogido: {new Date(detailShipment.fecha_recogida).toLocaleString('es')}</p>
                  )}
                </div>
                {/* Delivery Photos/Videos */}
                <div className="border-2 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-base text-gray-700 flex items-center gap-2">
                      <Camera className="h-5 w-5 text-green-500" /> Fotos/Videos de Entrega
                      {(detailShipment.fotos_entrega?.length || 0) > 0 && (
                        <Badge variant="secondary" className="ml-1">{detailShipment.fotos_entrega.length}</Badge>
                      )}
                    </h4>
                    <Button
                      data-testid="upload-delivery-photo-btn"
                      variant="outline" size="sm"
                      onClick={() => handlePhotoUpload(detailShipment.id, 'entrega')}
                      disabled={uploadingPhoto}
                      className="border-green-300 text-green-600"
                    >
                      <Camera className="mr-1 h-4 w-4" />
                      {uploadingPhoto ? 'Subiendo...' : 'Agregar'}
                    </Button>
                  </div>
                  <MediaGallery
                    items={detailShipment.fotos_entrega?.length ? detailShipment.fotos_entrega : (detailShipment.foto_entrega ? [detailShipment.foto_entrega] : [])}
                    emptyText="Sin fotos/videos de entrega"
                  />
                  {detailShipment.fecha_entrega && (
                    <p className="text-sm text-gray-500 text-center mt-2">Entregado: {new Date(detailShipment.fecha_entrega).toLocaleString('es')}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Relink Customer Dialog */}
        <Dialog open={relinkDialog.open} onOpenChange={(open) => { if (!open) setRelinkDialog({ open: false, shipmentId: null, field: null }); }}>
          <DialogContent className="max-w-lg" data-testid="relink-dialog" aria-describedby="relink-dialog-desc">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Link className="h-5 w-5 text-blue-500" />
                Enlazar {relinkDialog.field === 'remitente' ? 'Remitente' : 'Destinatario'}
              </DialogTitle>
              <p id="relink-dialog-desc" className="text-sm text-gray-500">Busca y selecciona el cliente correcto para esta factura</p>
            </DialogHeader>
            <div className="pt-2">
              <CustomerSearch
                customers={relinkDialog.field === 'remitente' ? remitentes : destinatarios}
                value=""
                onChange={(id) => handleRelink(id)}
                placeholder={`Buscar ${relinkDialog.field === 'remitente' ? 'remitente' : 'destinatario'}...`}
                colorScheme={relinkDialog.field === 'remitente' ? 'blue' : 'green'}
                testId="relink-customer-search"
              />
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Edit Customer Dialog */}
        <QuickEditDialog
          open={quickEditDialog.open}
          onOpenChange={(open) => { if (!open) setQuickEditDialog({ open: false, customer: null, shipmentId: null }); }}
          customer={quickEditDialog.customer}
          form={quickEditForm}
          setForm={setQuickEditForm}
          onSave={handleQuickEditSave}
          saving={savingQuickEdit}
          getMissingFields={getMissingFields}
        />

        {/* Batch Relink Dialog */}
        <BatchRelinkDialog
          open={batchRelinkOpen}
          onOpenChange={setBatchRelinkOpen}
          remitentes={remitentes}
          destinatarios={destinatarios}
          onComplete={() => {
            fetchShipments();
            checkOrphans();
          }}
        />
      </div>
    </div>
  );
};

export default Shipments;
